import {Assignment, Event, Step} from "types/workflowDetailsWizard";
import {StatusType} from "types/workflowDetailsWizardStep";
import {TagType} from "components/common/Tag.component";
import {StepTag} from "types/newWorkflowDetails";

export const APPROVAL_INITIATED_KEY = "approval-initiated";
export const APPROVAL_COMPLETED_KEY = "approval-completed";

const ACTIVE_STATUSES: Array<string> = [StatusType.ACTIVE, StatusType.CURRENT];
// Same tie-break as the old history table: events at the same moment keep the workflow's order
const EVENT_TYPE_ORDER = ["START", "USER_ACTION", "END"];

export const isActiveStep = (step?: Step) => !!step && ACTIVE_STATUSES.includes(step.status);

const byDate = (first: Event, second: Event) =>
    (new Date(first.date).getTime() - new Date(second.date).getTime()) ||
    (EVENT_TYPE_ORDER.indexOf(first.eventType) - EVENT_TYPE_ORDER.indexOf(second.eventType));

/**
 * Splits the workflow history over the steps it happened in, so each step can list its own events.
 * The old history table showed START as "Approval initiated" and END as "Approval completed", so those
 * get items of their own. Events only carry the step name, and names need not be unique, so steps are
 * matched walking forward through the workflow as the events advance; a restarted workflow (the name is
 * only found earlier) jumps back to the first step with that name. Events naming a step that is gone
 * go to the step the history last matched, as that is where the workflow stood at the time; before any
 * step has been matched that is the approval initiated item.
 */
export function groupEventsByStep(events: Array<Event>, steps: Array<Step>): Map<string, Array<Event>> {
    const groups = new Map<string, Array<Event>>();
    const add = (key: string, event: Event) => groups.set(key, [...(groups.get(key) ?? []), event]);

    let lastMatchedKey = APPROVAL_INITIATED_KEY;
    const findStep = (name: string, from: number) => {
        const ahead = steps.findIndex((step, index) => index >= from && step.name === name);
        return ahead !== -1 ? ahead : steps.findIndex((step) => step.name === name);
    }

    let currentStepIndex = 0;
    [...events].sort(byDate).forEach((event) => {
        if (event.eventType === "START") {
            add(APPROVAL_INITIATED_KEY, event);
            return;
        }
        if (event.eventType === "END") {
            add(APPROVAL_COMPLETED_KEY, event);
            return;
        }

        const index = findStep(event.stepName, currentStepIndex);
        if (index === -1) {
            add(lastMatchedKey, event);
            return;
        }
        currentStepIndex = index;
        lastMatchedKey = stepKey(steps[index]);
        add(lastMatchedKey, event);
    });

    return groups;
}

/** The status the completed workflow ended in, from its END event */
export function completedStatus(endEvent?: Event): StatusType | null {
    switch (endEvent?.action) {
        case "APPROVED":
            return StatusType.APPROVED;
        case "REJECTED":
            return StatusType.REJECTED;
        case "CANCELED":
            return StatusType.CANCELED;
        default:
            return null;
    }
}

export const stepKey = (step: Step) => `step-${step.stepNumber}`;

/** "A", "A and B", "A, B and C", "A, B and 3 more" */
export function joinNames(names: Array<string>, and: string, more: string, maxNames = 3): string {
    if (names.length <= 1) return names[0] ?? "";

    if (names.length > maxNames) {
        const shown = names.slice(0, maxNames - 1);
        return `${shown.join(", ")} ${and} ${names.length - shown.length} ${more}`;
    }

    return `${names.slice(0, -1).join(", ")} ${and} ${names[names.length - 1]}`;
}

export const assignmentNames = (assignments: Array<Assignment> = []) =>
    assignments.map((assignment) => `${assignment.userDTO.firstName} ${assignment.userDTO.lastName}`);

/** The backend sends the reason as "Reason: <newest>\n<older>", newest line last */
export function assignmentReasons(assignment: Assignment): Array<string> {
    return (assignment.explanation ?? "")
        .replace("Reason: ", "")
        .split("\n")
        .filter((part) => part.trim() !== "")
        .reverse();
}

export function stepTag(status: StatusType): StepTag | null {
    switch (status) {
        case StatusType.PASSED:
        case StatusType.APPROVED:
            return {type: TagType.SUCCESS, translationKey: "taskDetail.stepStatus.APPROVED"};
        case StatusType.ACTIVE:
        case StatusType.CURRENT:
            return {type: TagType.INFORMATION, translationKey: "taskDetail.stepStatus.IN_PROCESS"};
        case StatusType.FUTURE:
            return {type: TagType.DISABLED, translationKey: "taskDetail.stepStatus.NOT_STARTED"};
        case StatusType.SKIPPED:
            return {type: TagType.DISABLED, translationKey: "taskDetail.stepStatus.SKIPPED"};
        case StatusType.REJECTED:
            return {type: TagType.ERROR, translationKey: "taskDetail.stepStatus.REJECTED"};
        case StatusType.CANCELED:
            return {type: TagType.WARNING, translationKey: "taskDetail.stepStatus.CANCELED"};
        default:
            return null;
    }
}
